import React, { useState, useRef, useCallback, useEffect } from 'react';

// ── Signature canvas ──────────────────────────────────────────────────────────
function FirmaCanvas({ onFirma }) {
  const ref     = useRef(null);
  const drawing = useRef(false);
  const [signed, setSigned] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches?.[0] || e;
    return {
      x: (src.clientX - rect.left) * (canvas.width  / rect.width),
      y: (src.clientY - rect.top)  * (canvas.height / rect.height),
    };
  };

  const start = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = ref.current;
    const ctx    = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = ref.current;
    const ctx    = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#111827'; ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.stroke();
    if (!signed) setSigned(true);
  }, [signed]);

  const end = useCallback(() => {
    drawing.current = false;
    if (signed) onFirma(ref.current.toDataURL('image/png'));
  }, [signed, onFirma]);

  const clear = () => {
    const canvas = ref.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
    onFirma(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        width={600} height={160}
        className="w-full border-2 border-dashed border-gray-300 rounded-xl bg-white cursor-crosshair"
        style={{ touchAction: 'none' }}
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
      />
      <div className="flex justify-between items-center mt-1.5">
        <p className="text-xs text-gray-400">Firme con el dedo o el ratón</p>
        <button onClick={clear} className="text-xs text-gray-500 hover:text-red-600 transition-colors underline">
          Borrar y repetir
        </button>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtFecha(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function fmtImporte(n) {
  return Number(n || 0).toFixed(2).replace('.', ',') + ' €';
}

function calcTotal(lineas) {
  const base = (lineas || []).reduce((s, l) => s + Number(l.precio || 0) * Number(l.cantidad || 0), 0);
  return base * 1.21;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FirmaPresupuesto({ token }) {
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [pres,       setPres]       = useState(null);
  const [dni,        setDni]        = useState('');
  const [dniOk,      setDniOk]      = useState(false);
  const [email,      setEmail]      = useState('');
  const [emailOk,    setEmailOk]    = useState(false);
  const [firma,      setFirma]      = useState(null);
  const [consent,    setConsent]    = useState(false);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);

  useEffect(() => {
    if (!token) { setError('Token no válido.'); setLoading(false); return; }
    fetch(`/.netlify/functions/procesarFirmaPresupuesto?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setPres(d.presupuestoData);
          setEmail(d.presupuestoData.clienteEmail || '');
        } else {
          setError(d.error || 'No se pudo cargar el presupuesto.');
        }
      })
      .catch(() => setError('Error de conexión. Inténtelo de nuevo.'))
      .finally(() => setLoading(false));
  }, [token]);

  const validateDni = (val) => {
    const clean = val.trim().toUpperCase();
    return clean.length >= 4 && clean.length <= 20 && /^[A-Z0-9\-]{4,20}$/.test(clean);
  };

  const handleDniChange = (e) => {
    const val = e.target.value.toUpperCase();
    setDni(val);
    setDniOk(validateDni(val));
    setErrors(p => ({ ...p, dni: '' }));
  };

  const handleEmailChange = (e) => {
    const val = e.target.value.toLowerCase();
    setEmail(val);
    setEmailOk(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()));
    setErrors(p => ({ ...p, email: '' }));
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!validateDni(dni)) errs.dni = 'Introduce tu DNI, NIE o pasaporte (4–20 caracteres)';
    if (!emailOk)          errs.email = 'Introduce un email válido';
    if (!firma)            errs.firma = 'Dibuja tu firma en el recuadro';
    if (!consent)          errs.consent = 'Debes aceptar el presupuesto para continuar';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const resp = await fetch('/.netlify/functions/procesarFirmaPresupuesto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, dni: dni.trim().toUpperCase(), email: email.trim().toLowerCase(), firmaBase64: firma }),
      });
      const data = await resp.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setErrors({ submit: data.error || 'Error al procesar la firma. Inténtelo de nuevo.' });
      }
    } catch {
      setErrors({ submit: 'Error de conexión. Inténtelo de nuevo.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Cargando presupuesto…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Enlace no disponible</h2>
          <p className="text-gray-600 text-sm mb-6">{error}</p>
          <p className="text-gray-500 text-sm">
            📞 <a href="tel:966766306" className="text-red-600 font-semibold">966 766 306</a>
          </p>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Presupuesto aceptado</h2>
          <p className="text-gray-600 mb-4">
            Su firma ha sido registrada correctamente. Le enviaremos una confirmación a <strong>{email}</strong>.
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 mb-4">
            Nos pondremos en contacto con usted en breve para coordinar los próximos pasos.
          </div>
          <p className="text-gray-500 text-sm">
            📞 <a href="tel:966766306" className="text-red-600 font-semibold">966 766 306</a>&nbsp;·&nbsp;
            ✉️ <a href="mailto:info@climatizacionzapata.es" className="text-red-600 font-semibold">info@climatizacionzapata.es</a>
          </p>
        </div>
      </div>
    );
  }

  const total     = pres.total != null ? Number(pres.total) : calcTotal(pres.lineas);
  const base      = total / 1.21;
  const iva       = total - base;
  const validez   = pres.createdAt ? (() => {
    const d = new Date(pres.createdAt);
    d.setDate(d.getDate() + (pres.validezDias || 30));
    return d.toISOString().split('T')[0];
  })() : null;

  const inputCls = (err) =>
    `w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none transition-colors ${
      err ? 'border-red-400 focus:border-red-500 bg-red-50' : 'border-gray-300 focus:border-red-400'
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-red-600 px-6 py-5 text-white">
        <h1 className="text-xl font-bold">Climatización Zapata</h1>
        <p className="text-red-200 text-sm mt-0.5">Firma digital de presupuesto</p>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4 pb-12">

        {/* Resumen del presupuesto */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Presupuesto</p>
            {pres.numero && <span className="text-sm font-bold text-gray-700">{pres.numero}</span>}
          </div>
          {pres.titulo && <p className="font-semibold text-gray-800 mb-3">{pres.titulo}</p>}

          {/* Datos cliente */}
          <div className="text-sm text-gray-600 space-y-1 mb-4">
            {pres.clienteNombre    && <div><span className="text-gray-400">Cliente: </span>{pres.clienteNombre}</div>}
            {pres.clienteDireccion && <div><span className="text-gray-400">Dirección: </span>{pres.clienteDireccion}</div>}
            {pres.createdAt        && <div><span className="text-gray-400">Fecha: </span>{fmtFecha(pres.createdAt.split('T')[0])}</div>}
            {validez               && <div><span className="text-gray-400">Válido hasta: </span>{fmtFecha(validez)}</div>}
          </div>

          {/* Líneas */}
          {(pres.lineas || []).length > 0 && (
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-1 text-xs text-gray-400 font-bold">Descripción</th>
                  <th className="text-right py-1 text-xs text-gray-400 font-bold">Precio</th>
                  <th className="text-center py-1 text-xs text-gray-400 font-bold">Ud.</th>
                  <th className="text-right py-1 text-xs text-gray-400 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {pres.lineas.map((l, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5 text-gray-700">{l.descripcion}</td>
                    <td className="py-1.5 text-right text-gray-600">{fmtImporte(l.precio)}</td>
                    <td className="py-1.5 text-center text-gray-600">{l.cantidad}</td>
                    <td className="py-1.5 text-right font-semibold">{fmtImporte(Number(l.precio) * Number(l.cantidad))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Totales */}
          <div className="text-right space-y-1 text-sm border-t border-gray-100 pt-3">
            <div className="text-gray-500">Base: <span className="font-semibold text-gray-800">{fmtImporte(base)}</span></div>
            <div className="text-gray-500">IVA 21%: <span className="font-semibold text-gray-800">{fmtImporte(iva)}</span></div>
            <div className="text-base font-bold text-gray-900">Total: {fmtImporte(total)}</div>
          </div>

          {/* Condiciones */}
          {pres.condiciones && (
            <div className="mt-4 text-xs text-gray-500 border-t border-gray-100 pt-3">
              <p className="font-semibold text-gray-600 mb-1">Condiciones</p>
              <p className="whitespace-pre-wrap">{pres.condiciones}</p>
            </div>
          )}
        </div>

        {/* Formulario de firma */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-5">
          <h3 className="font-bold text-gray-800">Datos para la firma</h3>

          {/* DNI */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              DNI / NIE / Pasaporte *
            </label>
            <input
              type="text"
              value={dni}
              onChange={handleDniChange}
              placeholder="12345678A"
              maxLength={20}
              className={inputCls(errors.dni)}
            />
            {dniOk && !errors.dni && <p className="text-xs text-green-600 mt-1">✓ Documento identificado</p>}
            {errors.dni && <p className="text-xs text-red-600 mt-1">{errors.dni}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Email de confirmación *
            </label>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="nombre@email.com"
              className={inputCls(errors.email)}
            />
            <p className="text-xs text-gray-400 mt-1">Recibirá la confirmación de aceptación en este email</p>
            {emailOk && !errors.email && <p className="text-xs text-green-600 mt-1">✓ Email verificado</p>}
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>

          {/* Firma canvas */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Firma *
            </label>
            <FirmaCanvas onFirma={setFirma} />
            {firma && !errors.firma && <p className="text-xs text-green-600 mt-1">✓ Firma registrada</p>}
            {errors.firma && <p className="text-xs text-red-600 mt-1">{errors.firma}</p>}
          </div>

          {/* Consentimiento */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={e => { setConsent(e.target.checked); setErrors(p => ({ ...p, consent: '' })); }}
              className="mt-0.5 w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-700">
              He revisado el presupuesto y acepto las condiciones y el importe total de{' '}
              <strong>{fmtImporte(total)}</strong> (IVA incluido).
            </span>
          </label>
          {errors.consent && <p className="text-xs text-red-600 -mt-3">{errors.consent}</p>}

          {/* Error de submit */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {errors.submit}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {submitting
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Procesando…</>
              : '✍️ Firmar y aceptar presupuesto'
            }
          </button>

          <p className="text-xs text-gray-400 text-center">
            Al firmar, acepta el presupuesto y autoriza a Climatización Zapata a proceder con los trabajos.
            Sus datos serán tratados según el RGPD.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 space-y-1 pb-4">
          <p>Climatización Zapata · SAT oficial Ariston</p>
          <p>
            <a href="tel:966766306" className="text-red-600">966 766 306</a>
            {' · '}
            <a href="mailto:info@climatizacionzapata.es" className="text-red-600">info@climatizacionzapata.es</a>
          </p>
        </div>
      </div>
    </div>
  );
}
